
import { api } from './base';
import type { ApiResponse } from './base';
import { withIdempotencyKey } from './idempotency';
import { getValidToken } from '@/features/auth';
import { config } from '@/shared/lib/config';
import { compressImageFile } from '@/shared/lib/compress-image';
import type {
  Ticket,
  TicketMessage,
  MessageAttachment,
  TicketAssignee,
  TicketParticipant,
  TicketApproval,
  TicketEvent,
  TicketDetailData,
  TicketAccessScope,
  AllowedTransition,
  TicketWorkflowGraph,
  ApprovalRoundProgress,
} from '@/entities/ticket';
import type { TicketPriority } from '@/shared/config';
import type { CommitteeBrief } from './approval-committees';
import type { FeedbackKind } from './categories';

export interface TakeStatusUnchanged {
  reason: 'no_group' | 'no_path' | 'error';
  message: string;
}

export interface AssignMeta {
  statusUnchanged?: TakeStatusUnchanged;
  groupMoved?: boolean;
}

export interface TicketsListParams {
  limit?: number;
  offset?: number;
  q?: string;
  status?: string;
  priority?: TicketPriority | string;
  categoryId?: string;
  categoryIds?: string[];
  subcategoryIds?: string[];
  groupId?: string;
  massIncident?: 'only' | 'hide' | (string & {});
  slaLevel?: 'burning' | 'breached';
  myClients?: boolean;
  assignee?: 'me' | 'unassigned' | 'involved';
  sort?: 'createdAt' | 'updatedAt' | 'statusChangedAt' | 'priority' | 'slaDue';
  dir?: 'asc' | 'desc';
  assigneeId?: string;
  creatorId?: string;
  view?: 'pool' | 'mine' | 'approvals';
  includeClosed?: boolean;
}

export interface TicketStats {
  total: number;
  open: number;
  byStatus: Record<string, number>;
  statusLabels?: Record<string, string>;
  byPriority: Record<string, number>;
  myAssigned: number;
  unassigned: number;
  newToday: number;
  byGroup: { groupId: string | null; name: string | null; count: number }[];
  slaBurning: number;
  slaBreached: number;
}

export interface CreateTicketDto {
  title?: string;
  description: string;
  priority?: TicketPriority;
  categoryId?: string;
  subcategoryId?: string;
  clientInfo?: Record<string, unknown>;
  formData?: Record<string, unknown>;
  asDraft?: boolean;
  report?: FeedbackKind;
}

function toParams(p?: TicketsListParams): Record<string, string | number | boolean | undefined> | undefined {
  if (!p) return undefined;
  const out: Record<string, string | number | boolean | undefined> = {};
  if (p.limit != null) out.limit = p.limit;
  if (p.offset != null) out.offset = p.offset;
  if (p.q) out.q = p.q;
  if (p.status) out.status = p.status;
  if (p.priority) out.priority = p.priority;
  if (p.categoryId) out.categoryId = p.categoryId;
  if (p.categoryIds?.length) out.categoryIds = p.categoryIds.join(',');
  if (p.subcategoryIds?.length) out.subcategoryIds = p.subcategoryIds.join(',');
  if (p.groupId) out.groupId = p.groupId;
  if (p.massIncident) out.massIncident = p.massIncident;
  if (p.slaLevel) out.slaLevel = p.slaLevel;
  if (p.myClients) out.myClients = 'true';
  if (p.assignee) out.assignee = p.assignee;
  if (p.sort) out.sort = p.sort;
  if (p.dir) out.dir = p.dir;
  if (p.assigneeId) out.assigneeId = p.assigneeId;
  if (p.creatorId) out.creatorId = p.creatorId;
  if (p.view) out.view = p.view;
  if (p.includeClosed) out.includeClosed = true;
  return Object.keys(out).length ? out : undefined;
}

export type MessageKind = 'chat' | 'notes';

export interface TicketListView {
  id: string;
  name: string;
  query: Record<string, string>;
  sortOrder: number;
  preset: string | null;
  hidden: boolean;
}

export const ticketViewsApi = {
  list: () => api.get<TicketListView[]>('/api/v1/tickets/views').then((r) => r.data ?? []),
  create: (body: { name: string; query: Record<string, string> }) =>
    api
      .post<TicketListView>('/api/v1/tickets/views', body, { toast: { success: 'Набор сохранён' } })
      .then((r) => r.data),
  update: (
    id: string,
    body: { name?: string; query?: Record<string, string>; sortOrder?: number; hidden?: boolean },
  ) =>
    api.patch<TicketListView>(`/api/v1/tickets/views/${id}`, body).then((r) => r.data),
  remove: (id: string) => api.delete(`/api/v1/tickets/views/${id}`).then(() => undefined),
};

const workflowCache = new Map<string, ApiResponse<TicketWorkflowGraph>>();

export const ticketsApi = {
  list: (params?: TicketsListParams) =>
    api.get<Ticket[]>('/api/v1/tickets', toParams(params)),

  stats: () => api.get<TicketStats>('/api/v1/tickets/stats'),

  myPendingApprovals: () =>
    api.get<{ pending: number }>('/api/v1/tickets/my-approvals/count'),

  get: (id: string) => api.get<TicketDetailData>(`/api/v1/tickets/${id}`),

  getMute: (id: string) =>
    api.get<{ muted: boolean; canMute: boolean }>(`/api/v1/tickets/${id}/mute`),
  mute: (id: string) => api.put<{ muted: boolean }>(`/api/v1/tickets/${id}/mute`, {}),
  unmute: (id: string) => api.delete<{ muted: boolean }>(`/api/v1/tickets/${id}/mute`),

  create: (dto: CreateTicketDto) =>
    withIdempotencyKey('tickets:create', dto, (idempotencyKey) =>
      api.post<Ticket>('/api/v1/tickets', dto, { idempotencyKey }),
    ),

  update: (
    id: string,
    data: Partial<CreateTicketDto> & {
      status?: string;
      assigneeId?: string;
      inputs?: Record<string, unknown>;
    },
  ) => api.patch<Ticket>(`/api/v1/tickets/${id}`, data),

  setPlan: (id: string, patch: { workMinutes?: number | null; dueOn?: string | null }) =>
    api.patch<{ workMinutes: number | null; dueOn: string | null; editable: boolean }>(
      `/api/v1/tickets/${id}/plan`,
      patch,
    ),
  setClassification: (id: string, dto: { categoryId: string; subcategoryId: string | null }) =>
    api.put<{ changed: boolean; categoryId: string | null; subcategoryId: string | null }>(
      `/api/v1/tickets/${id}/classification`,
      dto,
    ),
  setComplexity: (id: string, cls: number, comment?: string) =>
    api.put<Record<string, unknown>>(`/api/v1/tickets/${id}/complexity`, {
      class: cls,
      ...(comment ? { comment } : {}),
    }),

  requestComplexity: (id: string, cls: number, comment?: string) =>
    api.post<Record<string, unknown>>(`/api/v1/tickets/${id}/complexity/request`, {
      class: cls,
      ...(comment ? { comment } : {}),
    }),

  decideComplexity: (id: string, approve: boolean) =>
    api.post<Record<string, unknown>>(`/api/v1/tickets/${id}/complexity/decide`, { approve }),

  getAllowedTransitions: (id: string) =>
    api.get<AllowedTransition[]>(`/api/v1/tickets/${id}/allowed-transitions`),

  getWorkflow: async (id: string, versionId?: string | null): Promise<ApiResponse<TicketWorkflowGraph>> => {
    const byVersion = versionId ? workflowCache.get(`v:${versionId}`) : undefined;
    if (byVersion) return byVersion;
    const byId = workflowCache.get(`t:${id}`);
    if (byId) return byId;
    const res = await api.get<TicketWorkflowGraph>(`/api/v1/tickets/${id}/workflow`);
    workflowCache.set(`t:${id}`, res);
    if (res.data.versionId) workflowCache.set(`v:${res.data.versionId}`, res);
    return res;
  },

  delete: (id: string) => api.delete<unknown>(`/api/v1/tickets/${id}`),

  getMessages: (id: string, params?: { kind?: MessageKind }) =>
    api.get<TicketMessage[]>(`/api/v1/tickets/${id}/messages`, params) as Promise<
      ApiResponse<TicketMessage[]> & { meta?: { total?: number; lastReadAt?: string | null } }
    >,

  mentionable: (id: string, kind?: MessageKind) =>
    api.get<Array<{ id: string; displayName: string }>>(
      `/api/v1/tickets/${id}/mentionable`,
      kind ? { kind } : undefined,
    ),

  addMessage: (
    id: string,
    body: string,
    attachmentIds?: string[],
    replyTo?: { messageId: string; attachmentId?: string } | null,
    internal = false,
  ) => {
    const payload = {
      body,
      ...(attachmentIds?.length ? { attachmentIds } : {}),
      ...(internal ? { internal: true } : {}),
      ...(replyTo
        ? {
            replyToMessageId: replyTo.messageId,
            ...(replyTo.attachmentId ? { replyToAttachmentId: replyTo.attachmentId } : {}),
          }
        : {}),
    };
    return withIdempotencyKey(`messages:${id}`, payload, (idempotencyKey) =>
      api.post<TicketMessage>(`/api/v1/tickets/${id}/messages`, payload, { idempotencyKey }),
    );
  },

  editMessage: (id: string, messageId: string, body: string) =>
    api.patch<TicketMessage>(`/api/v1/tickets/${id}/messages/${messageId}`, { body }),

  deleteMessages: (
    id: string,
    selection: { messageIds?: string[]; attachments?: Array<{ messageId: string; attachmentId: string }> },
  ) =>
    api.post<{
      deletedMessageIds: string[];
      updatedMessages: Array<{ id: string; attachments: MessageAttachment[] }>;
    }>(`/api/v1/tickets/${id}/messages/delete`, selection),

  deleteMessageAttachment: (id: string, messageId: string, attachmentId: string) =>
    api.delete<{ deletedMessage: boolean; attachments: MessageAttachment[] }>(
      `/api/v1/tickets/${id}/messages/${messageId}/attachments/${attachmentId}`,
    ),

  uploadAttachment: (
    ticketId: string,
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<MessageAttachment> =>
    new Promise((resolve, reject) => {
      Promise.all([getValidToken(), compressImageFile(file)])
        .then(([token, compressed]) => {
          const form = new FormData();
          form.append('file', compressed);
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${config.apiUrl}/api/v1/tickets/${ticketId}/attachments`);
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
          };
          xhr.onload = () => {
            try {
              const json = JSON.parse(xhr.responseText) as {
                data?: MessageAttachment;
                error?: { message?: string };
              };
              if (xhr.status >= 200 && xhr.status < 300 && json.data) resolve(json.data);
              else reject(new Error(json.error?.message ?? `Ошибка загрузки (${xhr.status})`));
            } catch {
              reject(new Error(`Ошибка загрузки (${xhr.status})`));
            }
          };
          xhr.onerror = () => reject(new Error('Сетевая ошибка при загрузке файла'));
          xhr.send(form);
        })
        .catch(reject);
    }),

  getEvents: (id: string, params?: { limit?: number; offset?: number; eventType?: string }) =>
    api.get<{ data: TicketEvent[]; total: number }>(`/api/v1/tickets/${id}/events`, params as Record<string, string | number | undefined>).then((r) => r.data),

  getHistory: (id: string, params?: { limit?: number; offset?: number; eventType?: string }) =>
    api
      .get<TicketEvent[]>(`/api/v1/tickets/${id}/events`, params as Record<string, string | number | undefined>)
      .then((r) => ({
        events: r.data ?? [],
        total: (r as unknown as { total?: number }).total ?? (r.data?.length ?? 0),
        isFiltered: (r as unknown as { isFiltered?: boolean }).isFiltered ?? false,
      })),

  getParticipants: (id: string) => api.get<TicketParticipant[]>(`/api/v1/tickets/${id}/participants`),

  addParticipant: (id: string, userId: string) =>
    api.post<TicketParticipant>(`/api/v1/tickets/${id}/participants`, { userId }),

  getAssignees: (id: string) => api.get<TicketAssignee[]>(`/api/v1/tickets/${id}/assignees`),

  addAssignee: (id: string, userId: string) =>
    api.post<TicketAssignee>(`/api/v1/tickets/${id}/assignees`, { userId }) as Promise<
      ApiResponse<TicketAssignee> & { meta?: AssignMeta }
    >,

  getApprovals: (id: string) => api.get<TicketApproval[]>(`/api/v1/tickets/${id}/approvals`),

  createApproval: (id: string, subject: string, approverId: string, accessScope?: TicketAccessScope) =>
    api.post<TicketApproval>(`/api/v1/tickets/${id}/approvals`, {
      subject,
      approverId,
      ...(accessScope ? { accessScope } : {}),
    }),

  approveApproval: (ticketId: string, approvalId: string, comment?: string) =>
    api.post<TicketApproval>(`/api/v1/tickets/${ticketId}/approvals/${approvalId}/approve`, {
      ...(comment ? { comment } : {}),
    }),

  rejectApproval: (ticketId: string, approvalId: string, comment?: string) =>
    api.post<TicketApproval>(`/api/v1/tickets/${ticketId}/approvals/${approvalId}/reject`, {
      ...(comment ? { comment } : {}),
    }),

  removeApproval: (ticketId: string, approvalId: string) =>
    api.delete<unknown>(`/api/v1/tickets/${ticketId}/approvals/${approvalId}`),

  approvalTarget: (ticketId: string) =>
    api.get<{ committee: CommitteeBrief | null }>(
      `/api/v1/tickets/${ticketId}/approval-target`,
    ),

  getApprovalRounds: (ticketId: string) =>
    api.get<ApprovalRoundProgress[]>(`/api/v1/tickets/${ticketId}/approval-rounds`),

  startApprovalRound: (
    ticketId: string,
    committeeId?: string,
    subject?: string,
    accessScope?: TicketAccessScope,
  ) =>
    api.post<ApprovalRoundProgress>(`/api/v1/tickets/${ticketId}/approval-rounds`, {
      ...(committeeId ? { committeeId } : {}),
      ...(subject ? { subject } : {}),
      ...(accessScope ? { accessScope } : {}),
    }),

  removeApprovalRound: (ticketId: string, roundId: string) =>
    api.delete<unknown>(`/api/v1/tickets/${ticketId}/approval-rounds/${roundId}`),

  deleteTicket: (id: string) => api.delete<unknown>(`/api/v1/tickets/${id}`),
  withdrawTicket: (id: string) => api.post<Ticket>(`/api/v1/tickets/${id}/withdraw`),

  getReads: (id: string) =>
    api.get<{ userId: string; displayName: string; lastReadAt: string }[]>(
      `/api/v1/tickets/${id}/reads`,
    ),

  confirmResolution: (id: string, rating: number, comment?: string) =>
    api.post<{ ok: true }>(`/api/v1/tickets/${id}/confirm`, {
      rating,
      ...(comment ? { comment } : {}),
    }),

  rejectResolution: (id: string, comment: string) =>
    api.post<{ ok: true }>(`/api/v1/tickets/${id}/reject-resolution`, { comment }),

  takeTicket: (id: string) =>
    api.post<TicketDetailData>(`/api/v1/tickets/${id}/take`) as Promise<
      ApiResponse<TicketDetailData> & { meta?: AssignMeta }
    >,

  replyMassIncident: (id: string, body: string, status?: string, ticketIds?: string[]) =>
    api.post<{
      incidentId: string;
      total: number;
      messaged: number;
      assigned: number;
      statusChanged: number;
      skipped: { ticketId: string; reason: string }[];
    }>(`/api/v1/tickets/${id}/mass-incident/reply`, {
      body,
      ...(status ? { status } : {}),
      ...(ticketIds ? { ticketIds } : {}),
    }),

  removeAssignee: (ticketId: string, userId: string) =>
    api.delete<unknown>(`/api/v1/tickets/${ticketId}/assignees/${encodeURIComponent(userId)}`),

  reassignGroup: (id: string, groupId: string) =>
    api.put<TicketDetailData>(`/api/v1/tickets/${id}/group`, { groupId }),

  reassignAssignee: (ticketId: string, fromUserId: string, toUserId: string, groupId?: string) =>
    api.put<TicketDetailData>(
      `/api/v1/tickets/${ticketId}/assignees/${encodeURIComponent(fromUserId)}`,
      { toUserId, ...(groupId ? { groupId } : {}) },
    ),
};
